/**
 * @module cercaliagl/Popup
 */

import maplibregl from 'maplibre-gl';

import { DEFAULT_POPUP_OFFSET } from './globals';
import { isNumber } from './util';

/**
 * @typedef {Object} PopupOptions
 * @property {string} [title] Title. If not specified. Element won't be created.
 * @property {string|HTMLElement} content
 * @property {string} [className] Class name for CSS styling. Default empty.
 * @property {boolean} [visible] Visible or not. Default `false`.
 * @property {Array.<number>} [anchor] Anchor.
 * @property {Array.<number> | number} [offset] Offset. You should change it if Marker icon has different size than default icon.
 * @property {number|string} [maxWidth] Max width in pixels. Or `none` value, to disable id.
 * @property {number} [closeOnTime_] If > 0, close the popup on the miliseconds specified.
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const popup = new cercaliagl.Popup({ ... });
 * ```
 *
 * Create a popup. Popup belongs to a marker.
 *
 * Example usage:
 *
 * ```js
 * const popup = new cercaliagl.Popup({
 *   title: 'Header',
 *   content: '<div>Hello <b>World!</b></div>',
 *   className: 'css-custom-class',
 *   visible: true,
 * });
 *
 * const marker = new cercaliagl.Marker({
 *   position: new cercaliagl.LonLat(1.41, 41.431),
 *   popup: popup
 * })
 *
 * map.addMarker(marker);
 * ``'
 */
class Popup {
  /**
   * @param {PopupOptions} options Options
   */
  constructor(options) {
    /**
     * @private
     * @type {string|HTMLElement}
     */
    this.content_ = options.content;

    /**
     * @private
     * @type {string|HTMLElement}
     */
    this.title_ = options.title ? options.title : null;

    /**
     * @private
     * @type {string}
     */
    this.className_ = options.className ? options.className : '';

    /**
     * @private
     * @type {boolean}
     */
    this.visible_ = options.visible !== undefined ? options.visible : false;

    /**
     * @private
     * @type {Array.<number>}
     */
    this.anchor_ = options.anchor ? options.anchor : undefined;

    /**
     * @private
     * @type {Array.<number>}
     */
    this.offset_ = options.offset ? options.offset : DEFAULT_POPUP_OFFSET;

    /**
     * @private
     * @type {number|string}
     */
    this.maxWidth_ = options.maxWidth ? options.maxWidth : 260;

    /**
     * @private
     * @type {number}
     */
    this.closeOnTime_ = options.closeOnTime ? options.closeOnTime : 0;

    /**
     * @private
     * @type {number}
     */
    this.currentTimeout_ = null;

    /**
     * @private
     * @type {maplibregl.Popup}
     */
    this.popup_ = null;

    this.initialize_();
  }

  /**
   * @return {boolean}
   */
  isVisible() {
    return this.visible_;
  }

  /**
   * Create internal element popup
   */
  initialize_() {
    const html = Popup.createHtml(this.title_, this.content_);
    this.popup_ = new maplibregl.Popup({
      className: `cercalia-popup${this.className_}`,
      closeOnClick: false,
      anchor: this.anchor_,
      offset: this.offset_,
      maxWidth: isNumber(this.maxWidth_) ? `${this.maxWidth_}px` : this.maxWidth_,
    }).setHTML(html);

    if (this.closeOnTime_ > 0) {
      this.popup_.on('open', () => {
        this.currentTimeout_ = setTimeout(() => {
          this.popup_.remove();
        }, this.closeOnTime_);
      });

      this.popup_.on('close', () => {
        if (this.currentTimeout_) {
          clearTimeout(this.currentTimeout_);
        }
      });
    }
  }

  /**
   * Create HTML from title and content
   * @param {string} title Title
   * @param {string} content Content
   * @private
   */
  static createHtml(title, content) {
    return `
    ${title ? `<div class="header">${title}</div>` : ''}
    <div class="content">${content}</div>
    `;
  }

  /**
   * Set title
   * @param {string} title Title
   * @api
   */
  setTitle(title) {
    this.title_ = title;
    this.popup_.setHTML(Popup.createHtml(this.title_, this.content_));
  }

  /**
   * Set content
   * @param {string} content Content
   * @api
   */
  setContent(content) {
    this.content_ = content;
    this.popup_.setHTML(Popup.createHtml(this.title_, this.content_));
  }

  /**
   * Get Popup. This object popup is from `maplibregl.Popup` class (https://docs.mapbox.com/mapbox-gl-js/api/markers/#popup)
   * @return {import("maplibre-gl").Popup} Mapboxgl Popup
   * @api
   */
  getMapboxPopup() {
    return this.popup_;
  }

  /**
   * Set popup position. Used internally Method no published
   * @param {import("./LonLat").default} position Position
   */
  setPosition(position) {
    this.popup_.setLngLat(position.toMapboxLngLat());
  }
}

export default Popup;
